/**
 * @file billReferenceLinks.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-10-14
 * @description "PO links from references" (Cam, 2026-10-14 — Burcombe's
 *              QA03476): a bill of several jobs, each quoting an SO-D, a
 *              PO-H, a bare "10183" or an INV- from before cutover. The
 *              server works out the PO each job should link to
 *              (POST /api/bills/reference-links, or `reference_links` on a
 *              scan — services/billReferenceLinkService); this is the screen's
 *              side: what to ask for, what starts ticked, and what Apply does
 *              to the bill's lines.
 *
 *              Apply (applyLinks):
 *                - a PO-H (the haulier's own PO) → each of the job's lines
 *                  bills it, as a PO-H's haulage line does (no PO line key,
 *                  account 320 when none is set) — an ordinary goods link;
 *                - a PO-D / PO- of the bill's own supplier → its lines are
 *                  brought onto the bill and the invoice's lines reconciled
 *                  with them, as Choose POs does (`loadPoIds`);
 *                - another supplier's PO (the old way: the Xero PO for the
 *                  goods) → a third-party haulage link (utils/billHaulageLink:
 *                  bills nothing), the job's lines kept with no PO;
 *                - "No PO" → the job's lines kept with no PO.
 *              A job's lines kept with no PO by this choice are marked
 *              `_refNoPo`: they do not count against Add Bill's "at most one
 *              line on no PO" (each was chosen, job by job). A charge line
 *              with no item and no account takes the haulage account (320),
 *              as a PO-H's haulage line does.
 *              Only ticked jobs change. Exact suggestions start ticked; a PO
 *              already billed by another bill never does (the PO Reference
 *              rule, utils/billPoReference.poBilledElsewhere).
 *
 *              Pure.
 */

import { poBilledElsewhere, billedPoSentence } from './billPoReference';

/** The account a PO-H's haulage line takes on a bill (bag haulage), as /generate-from-pos gives it. */
export const POH_ACCOUNT = '320';

export const CONFIDENCE_LABEL = Object.freeze({
  exact: 'Exact', likely: 'Likely', possible: 'Possible', none: 'No match',
});

/** The choice that keeps a job's lines with no PO. */
export const NO_PO = 'none';

const clean = (v) => String(v ?? '').trim();

/** A charge line's account: its own, else (no item) the haulage account. */
const haulageAccount = (l) => l.account_code || (l.inventory_item_id ? '' : POH_ACCOUNT);

/** What a read line's job block quoted, kept on the grid line as `_ref`. */
export function refFieldsOf(read) {
  if (!read || typeof read !== 'object') return null;
  const refs = Array.isArray(read.references) ? read.references.map(clean).filter(Boolean) : [];
  const out = {
    references: refs,
    job_key: clean(read.job_key) || null,
    deliver_to: clean(read.deliver_to) || null,
    job_date: clean(read.job_date) || null,
  };
  return refs.length || out.job_key || out.deliver_to ? out : null;
}

/**
 * The request for POST /api/bills/reference-links from the grid's lines:
 * each non-empty line by its place, with what its job quoted (`_ref`) or,
 * failing that, its words. `ids[i]` is the grid line of request line i.
 *
 * @param {object[]} lines — grid lines
 * @param {(line: object) => boolean} isEmpty
 */
export function referenceRequest(lines, isEmpty = () => false) {
  const real = (Array.isArray(lines) ? lines : []).filter(l => !isEmpty(l));
  return {
    ids: real.map(l => l.id),
    lines: real.map((l, index) => ({
      index,
      description: l.product_name || '',
      references: l._ref?.references || [],
      job_key: l._ref?.job_key || null,
      deliver_to: l._ref?.deliver_to || null,
      job_date: l._ref?.job_date || null,
    })),
  };
}

/** Do any of these lines quote anything to go on? Null when they do, else why not. */
export function suggestBlock(lines, isEmpty = () => false) {
  const real = (Array.isArray(lines) ? lines : []).filter(l => !isEmpty(l));
  if (!real.length) return 'Add the bill\'s lines first (upload the invoice)';
  const any = real.some(l => l._ref || /\b(?:PO|SO|INV)-[A-Z]?\d+/i.test(String(l.product_name || '')));
  return any ? null : 'No line quotes an order, PO or invoice — upload the invoice PDF so its references are read';
}

/** A suggestion target as the PO Reference rule reads a matched PO. */
const asMatchedPo = (t) => (t ? { id: t.po_id, purchase_order_number: t.purchase_order_number, status: t.status, billed_by: t.billed_by || [] } : null);

/**
 * Already billed by another bill — a possible double billing. Goods links use
 * the PO Reference rule; a haulage link, another bill's haulage link to it.
 * Null, or the sentence to show.
 */
export function billedWarning(target, { billId = null } = {}) {
  if (!target) return null;
  if (target.link === 'haulage') {
    const self = billId != null && billId !== '' ? Number(billId) : null;
    const others = (target.haulage_billed_by || []).filter(b => Number(b?.id) !== self);
    if (!others.length) return null;
    return `${target.purchase_order_number || 'The PO'} already has a haulage bill (${others.map(b => `Bill ${b.supplier_reference || b.bill_number || `#${b.id}`}`).join(', ')}) — possible double billing.`;
  }
  const billed = poBilledElsewhere(asMatchedPo(target), { billId });
  return billed ? `${billedPoSentence(billed)} Possible double billing.` : null;
}

/** The candidate a job's choice names (null for No PO / none chosen). */
export function chosenTarget(job, choice) {
  if (!job || !choice || choice.po_id === NO_PO || choice.po_id == null) return null;
  return (job.candidates || []).find(c => Number(c.po_id) === Number(choice.po_id)) || null;
}

/**
 * What starts chosen and ticked: each job's best suggestion; ticked only when
 * exact, with no warning and not already billed by another bill.
 *
 * @returns {Object<string, { po_id: number|'none'|null, tick: boolean }>}
 */
export function initialChoices(jobs, { billId = null } = {}) {
  const out = {};
  for (const j of jobs || []) {
    const t = j.target || null;
    out[j.job_key] = {
      po_id: t ? t.po_id : (j.confidence === 'none' ? NO_PO : null),
      tick: !!t && j.tick === true && !billedWarning(t, { billId }),
    };
  }
  return out;
}

/** The picker's options for one job: its candidates, then No PO. */
export function candidateOptions(job) {
  const opts = (job?.candidates || []).map(c => ({
    value: c.po_id,
    label: `${c.purchase_order_number || `PO #${c.po_id}`} — ${CONFIDENCE_LABEL[c.confidence] || c.confidence}`
      + `${c.link === 'haulage' ? ' (third-party haulage)' : ''}`,
  }));
  opts.push({ value: NO_PO, label: 'No PO — keep the lines without one' });
  return opts;
}

/** How many jobs Apply would change. */
export const tickedCount = (jobs, choices) => (jobs || []).filter(j => choices?.[j.job_key]?.tick
  && (choices[j.job_key].po_id === NO_PO || chosenTarget(j, choices[j.job_key]))).length;

/**
 * Apply the ticked jobs' choices to the grid's lines.
 *
 * @param {object[]} lines — grid lines
 * @param {object[]} jobs — the server's suggestions (line_indexes into `ids`)
 * @param {object} choices — { [job_key]: { po_id, tick } }
 * @param {string[]} ids — grid line id of each request line (referenceRequest().ids)
 * @param {{ reconciling?: boolean }} [opts] — reconciling: a new bill whose
 *        invoice lines are matched to its POs' lines (a PO-D's lines are then
 *        brought on and matched); otherwise a PO-D is set on the lines as is
 * @returns {{ lines: object[], goodsPoIds: number[], loadPoIds: number[], haulagePos: object[], applied: number, linesLinked: number }}
 */
export function applyLinks(lines, jobs, choices, ids, { reconciling = false } = {}) {
  const byId = new Map();
  const goodsPoIds = [];
  const loadPoIds = [];
  const haulagePos = [];
  let applied = 0;
  const addOnce = (arr, v) => { if (!arr.includes(v)) arr.push(v); };
  for (const job of jobs || []) {
    const choice = choices?.[job.job_key];
    if (!choice?.tick) continue;
    const lineIds = (job.line_indexes || []).map(i => ids?.[i]).filter(Boolean);
    if (!lineIds.length) continue;
    const refs = (job.references || []).join(', ');
    if (choice.po_id === NO_PO) {
      for (const id of lineIds) byId.set(id, (l) => ({
        ...l, purchase_order_id: null, po_line_key: null, purchase_order_number: null,
        _poHaulageLine: false, _noPo: true, _refNoPo: true, _fromPdf: false, _highlight: true,
        account_code: haulageAccount(l),
        _hint: `${refs || 'This job'}: kept without a PO`,
      }));
      applied += 1;
      continue;
    }
    const t = chosenTarget(job, choice);
    if (!t) continue;
    applied += 1;
    const poId = Number(t.po_id);
    if (t.link === 'haulage') {
      if (!haulagePos.some(p => p.id === poId)) {
        haulagePos.push({ id: poId, purchase_order_number: t.purchase_order_number, xero_purchase_order_id: t.xero_purchase_order_id || null, contact_name: t.contact_name || null });
      }
      for (const id of lineIds) byId.set(id, (l) => ({
        ...l, purchase_order_id: null, po_line_key: null, purchase_order_number: null,
        _poHaulageLine: false, _noPo: true, _refNoPo: true, _fromPdf: false, _highlight: true,
        account_code: haulageAccount(l),
        _hint: `Third-party haulage for ${t.purchase_order_number} (${refs})`,
      }));
      continue;
    }
    if (t.po_type === 'haulage' || !reconciling) {
      addOnce(goodsPoIds, poId);
      for (const id of lineIds) byId.set(id, (l) => ({
        ...l,
        purchase_order_id: poId,
        po_line_key: null,
        purchase_order_number: t.purchase_order_number,
        _poHaulageLine: t.po_type === 'haulage',
        _noPo: false,
        _refNoPo: false,
        _fromPdf: false,
        account_code: t.po_type === 'haulage' ? haulageAccount(l) : (l.account_code || ''),
        _highlight: true,
        _hint: `Linked to ${t.purchase_order_number} — ${t.reason}`,
      }));
      continue;
    }
    // A PO-D / PO- of the bill's own supplier on a scanned bill: its lines come
    // on and the invoice's lines are reconciled with them, as Choose POs does.
    addOnce(loadPoIds, poId);
  }
  const out = (Array.isArray(lines) ? lines : []).map(l => (byId.has(l.id) ? byId.get(l.id)(l) : l));
  return { lines: out, goodsPoIds, loadPoIds, haulagePos, applied, linesLinked: byId.size };
}

/**
 * Bulk Add Bills: the exact suggestions applied before Accept; every other
 * job is a reason to review. Only a PO-H link is made here — a PO-D's lines
 * are matched in review.
 *
 * @returns {{ lines: object[], poIds: number[], poMeta: object[], reasons: string[] }}
 */
export function bulkApply(lines, jobs, ids) {
  const choices = {};
  const reasons = [];
  const poMeta = [];
  for (const j of jobs || []) {
    const t = j.target;
    const ok = t && j.tick && t.link === 'goods' && t.po_type === 'haulage' && !billedWarning(t);
    choices[j.job_key] = { po_id: t ? t.po_id : null, tick: !!ok };
    if (ok) {
      if (!poMeta.some(p => p.id === t.po_id)) poMeta.push({ id: t.po_id, purchase_order_number: t.purchase_order_number, po_type: 'haulage', status: t.status });
      continue;
    }
    const refs = (j.references || []).join(', ') || 'A job with no reference';
    const warn = t ? billedWarning(t) : null;
    reasons.push(t
      ? `${refs} → ${t.purchase_order_number} (${(CONFIDENCE_LABEL[j.confidence] || j.confidence).toLowerCase()}${t.link === 'haulage' ? ', third-party haulage' : ''})${warn ? ` — ${warn}` : ''}: check it in review.`
      : `${refs}: ${j.reason}`);
  }
  const res = applyLinks(lines, jobs, choices, ids, { reconciling: false });
  return { lines: res.lines, poIds: res.goodsPoIds, poMeta, reasons };
}
